import React from 'react';
import { CompareFigure } from 'shared/ui/Figure';
import { RAIL_GROUP_LABELS, type RailGroup, type RailTile } from 'shared/state/railTypes';
import { useLiveRail } from './useLiveRail';
import { StatBlock } from './StatBlock';
import { PriceCard } from './PriceCard';

// Derived from the labels map (not restated by hand) so a fifth RailGroup added to
// railTypes.ts is a compile error here instead of silently rendering nowhere.
const ORDER = Object.keys(RAIL_GROUP_LABELS) as RailGroup[];

/** Tiles arrive as a prop: the registry lives in `app`, and a feature never
 *  imports `app` (guidelines §1). AppShell filters and hands them down, along with
 *  any system-wide `controls` for the stat block header and a `beside` panel to
 *  stand next to the stat block (the compact rack).
 *
 *  The rail is capped to the window (RailFrame), so it is laid out as a column:
 *  the stat block and the readouts scroll, and the price card sits under the
 *  scroller where it is always in view. */
export function LiveRail({ tiles, controls, beside }: { tiles: RailTile[]; controls?: React.ReactNode; beside?: React.ReactNode }) {
  const r = useLiveRail();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Focusable so the readouts can be scrolled from the keyboard. */}
      <div
        role="group"
        aria-label="System readouts"
        tabIndex={0}
        className="flex min-h-0 flex-col gap-4 overflow-y-auto [scrollbar-width:thin] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
      >
        <div data-testid="statblock-row" className="flex shrink-0 items-start gap-3">
          {beside}
          <div className="min-w-0 flex-1">
            <StatBlock sets={r.donuts} tally={r.tally} isPBUnit={r.isPBUnit} onUnitToggle={r.onUnitToggle} controls={controls} />
          </div>
        </div>

        {ORDER.map((group) => {
          const inGroup = tiles.filter((t) => t.group === group);
          if (inGroup.length === 0) return null;
          return (
            <section key={group} className="shrink-0 rounded-[var(--radius-2)] border border-hairline/15 bg-surface p-3">
              <h3 className="mb-2 font-display text-xs font-semibold text-ink-muted">
                {RAIL_GROUP_LABELS[group]}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {inGroup.map((t) => (
                  <CompareFigure
                    key={t.id}
                    label={t.label}
                    value={t.value(r.ctx)}
                    current={r.isAffected(t.keys) ? t.value(r.baselineCtx) : undefined}
                    unit={t.unit?.(r.ctx)}
                    hint={t.hint?.(r.ctx)}
                    tone={t.tone}
                    loading={r.isCalculating}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {r.showPrice && <div className="shrink-0"><PriceCard {...r.price} loading={r.isCalculating} /></div>}
    </div>
  );
}
